/* eslint-disable no-console */
/* eslint-disable no-void */
/* eslint-disable react/jsx-props-no-spreading */
/*

FLOW:
Frontend → sends images and sets PREPARING_UPLOAD
Backend → processes and sends statuses via WebSocket (SAVING_IMAGES → ANALYZING_IMAGES → PERSISTING_DATA)
Frontend → receives statuses via sockets.ts:47 and updates the toast notifications accordingly

1. Preparing images (PREPARING_UPLOAD)
handled by the frontend (useCreateObservation.tsx:61/181)
creates the FormData payload with images and metadata, then sends the files to the server via the /observations/bulk-create/ endpoint
displays the upload percentage (0–100%) during file transfer

2. Saving images (SAVING_IMAGES)
the backend saves the image files to storage
status received from the backend via WebSocket
files are persisted in the storage system

3. Analyzing images (ANALYZING_IMAGES)
the backend analyzes/processes the images
status received via WebSocket

4. Persisting images (PERSISTING_DATA)
the backend saves the analysis results to the database
status received via WebSocket
creates observation records linked to the uploaded images

5. Updating images (UPDATING_UI)
the frontend updates the UI (storeReducer.ts:295/443)
updates the observations list with the newly created items

*/
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import i18n from 'i18next'
import JSZip, { JSZipObject } from 'jszip'
import { useEffect, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { useStore } from '../../store/store'
import { ProductClass } from '../../types'
import {
  ALLOWED_IMAGE_TYPES,
  ALLOWED_MIME_TYPES,
  ALLOWED_TYPES,
  MAX_FILE_SIZE,
  MAX_FILES,
  MIN_FILE_SIZE,
  WITH_PRODUCT_CLASS,
} from './definitions'
import ImageGroupsModal from './ImageGroupsModal/ImageGroupsModal'
import { CandidateClassImage, UploadImageGroup } from './types'
import useStyles, {
  acceptStyle,
  baseStyle,
  disabledStyle,
  focusedStyle,
  rejectStyle,
} from './UploadObservations.styles'
import useCreateObservation from './useCreateObservation'
import {
  getDirectoriesFromPath,
  parseAndValidateMapping,
  readFileAsText,
} from './utils'

// Logging prefix for easy filtering in console
const LOG_PREFIX = '[UploadObservations]'

interface UploadObservationsProps {
  productId: number
  productType: string
}

const UploadObservations = ({
  productId,
  productType,
}: UploadObservationsProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { product } = useStore()

  const { createObservation } = useCreateObservation()
  const [isValidating, setIsValidating] = useState<boolean>(false)

  // Enable product class selection if supported
  // (eg. anomaly, classification)
  const withProductClass = WITH_PRODUCT_CLASS.includes(productType)

  // The product class selected by the user
  // - Can be undefined (for non-annotated images)
  const [productClass] = useState<ProductClass | undefined>()

  // Dropzone states
  const [dropRejected, setDropRejected] = useState<string>('')
  const [isDropzoneDisabled, setIsDropzoneDisabled] = useState<boolean>(false)
  const [droppedFiles, setDroppedFiles] = useState<File[]>([])

  // ZIP files helpers
  const [validatingZipFiles, setValidatingZipFiles] = useState<string[]>([])

  // Images parsed from zip files with user defined classes
  // Must be checked and associated to an existing product class
  const [candidateClassImages, setCandidateClassImages] = useState<
    CandidateClassImage[]
  >([])

  // Show modal when all zip files are validated
  const [showImageGroupsModal, setShowImageGroupsModal] = useState(false)

  // Upload status message
  const [uploadStatus, setUploadStatus] = useState<string>('')

  useEffect(() => {
    setUploadStatus(isValidating ? t('Preparing upload') : '')
  }, [isValidating, t])

  // helper for mime from file name
  const mimeFromName = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase()
    switch (ext) {
      case 'png':
        return 'image/png'
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg'
      case 'webp':
        return 'image/webp'
      case 'tif':
      case 'tiff':
        return 'image/tiff'
      case 'bmp':
        return 'image/bmp'
      case 'json':
        return 'application/json'
      default:
        return 'application/octet-stream'
    }
  }

  // entry JSZip to File conversion
  const entryToFile = async (entry: any, forceType?: string) => {
    const blob = await entry.async('blob')
    const name = entry.name.split('/').pop() || entry.name
    const type = forceType ?? mimeFromName(name)
    return new File([blob], name, { type })
  }

  useEffect(() => {
    if (droppedFiles.length === 0) return

    console.log(LOG_PREFIX, 'Processing dropped files', {
      count: droppedFiles.length,
      files: droppedFiles.map((f) => ({
        name: f.name,
        type: f.type,
        size: f.size,
      })),
    })

    // Check if any file is a zip file
    const zipFiles = droppedFiles.filter((file) => file.type.includes('zip'))

    if (zipFiles.length > 0) {
      if (zipFiles.length > 1) {
        cleanUp(t('Upload only one zip file at a time'))
        return
      }
      if (droppedFiles.length > 1) {
        cleanUp(t('Cannot upload images and zip files together'))
        return
      }

      if (
        productType === 'segmentation' ||
        productType === 'classification_patch'
      ) {
        void (async () => {
          try {
            const zip = await JSZip.loadAsync(zipFiles[0])

            // Check for mapping.json
            const mappingEntry = zip.file(/(^|\/)mapping\.json$/i)[0]

            // Check for files in images/ and masks/ directories
            const allEntries: any[] = Object.values(zip.files)
            const imageEntries = allEntries.filter(
              (e) => !e.dir && /(^|\/)images\//i.test(e.name)
            )
            const maskEntries = allEntries.filter(
              (e) => !e.dir && /(^|\/)masks\//i.test(e.name)
            )

            // ---- STEP 1: at least one condition is true ----
            const hasSomething =
              mappingEntry || imageEntries.length > 0 || maskEntries.length > 0

            if (!hasSomething) {
              // Standard file validation
              validateDroppedFiles([...droppedFiles])
              return
            }

            // ---- STEP 2: all conditions must true ----
            if (!mappingEntry) {
              cleanUp(t('Zip is missing mapping.json'))
              return
            }
            if (imageEntries.length === 0) {
              cleanUp(t('Zip is missing images/ folder or it is empty'))
              return
            }
            if (maskEntries.length === 0) {
              cleanUp(t('Zip is missing masks/ folder or it is empty'))
              return
            }

            // Extract files
            const mappingFile = await entryToFile(
              mappingEntry,
              'application/json'
            )

            // 👉 Validate mapping.json contents here
            try {
              const text = await readFileAsText(mappingFile)
              const mapping = JSON.parse(text) as Record<string, unknown>
              const mappingKeys = Object.keys(mapping)
              const productClasses = Array.isArray(product?.productClasses)
                ? product?.productClasses
                : []

              const missingInMapping = productClasses?.filter(
                (cls) => !mappingKeys?.includes(cls)
              )
              const extraInMapping = mappingKeys?.filter(
                (key) => !productClasses?.includes(key)
              )

              if (
                missingInMapping &&
                extraInMapping &&
                (missingInMapping.length > 0 || extraInMapping.length > 0)
              ) {
                throw new Error('Mapping keys do not match product classes')
              }

              parseAndValidateMapping(text)
            } catch (e) {
              const msg =
                e instanceof Error ? e.message : t('Invalid mapping.json')
              const translatedMsg = i18n.t(msg)
              cleanUp(translatedMsg)
              return
            }
            const imageFiles = await Promise.all(
              imageEntries.map((e) => entryToFile(e))
            )
            const maskFiles = await Promise.all(
              maskEntries.map((e) => entryToFile(e))
            )

            // Custom file validation
            validateDroppedFiles(imageFiles, mappingFile, maskFiles)
          } catch (err) {
            console.error('Error reading zip file:', err)
            cleanUp(t('Error reading zip file'))
          }
        })()
        return
      }
    }

    // Standard file validation
    validateDroppedFiles([...droppedFiles])

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedFiles])

  const handleZipFile = (zipFile: File) => {
    const zip = new JSZip()
    let error = false
    // Load ZIP data
    zip.loadAsync(zipFile).then(() => {
      zip.forEach((path: string, zippedFile: JSZipObject) => {
        if (!error) {
          const directories = getDirectoriesFromPath(path)
          const extension = path.split('.').pop()?.toLowerCase()

          if (zippedFile.dir) {
            // Skip directories
          } else if (!extension || !ALLOWED_IMAGE_TYPES.includes(extension)) {
            // Check if all files are images
            error = true
            cleanUp(t('Invalid file type'))
            toast.error(`${t('Invalid file type')}: ${zippedFile.name}`)
          } else {
            const hasClass = WITH_PRODUCT_CLASS.includes(productType)
            const candidateClass =
              hasClass && directories.length > 0
                ? directories[directories.length - 1]
                : undefined
            zip.files[path].async('blob').then((imageData) => {
              const image = new File([imageData], zippedFile.name)
              setCandidateClassImages((prev) => [
                ...prev,
                { image, candidateClass, mask: undefined, mapping: undefined },
              ])
            })
          }
        }
      })

      // Remove validate zip file from validating list
      setValidatingZipFiles((prev) =>
        prev.filter((name) => name !== zipFile.name)
      )
    })
  }

  const validateDroppedFiles = (
    files: File[],
    mapping?: File,
    masks?: File[]
  ) => {
    console.log(LOG_PREFIX, 'validateDroppedFiles called', {
      filesCount: files.length,
      hasMapping: !!mapping,
      masksCount: masks?.length || 0,
    })

    const imageFiles = files.filter((file) => !file.type.includes('zip'))
    const zipFiles = files.filter((file) => file.type.includes('zip'))
    const safeMasks = masks ?? []
    const hasMapping = !!mapping

    console.log(LOG_PREFIX, 'File breakdown', {
      imageFilesCount: imageFiles.length,
      zipFilesCount: zipFiles.length,
      hasMapping,
    })

    if (!hasMapping) {
      // Handle image files (non-zipped):
      // -> create imageGroup with selected class
      if (imageFiles.length > 0) {
        console.log(LOG_PREFIX, 'Adding image files to candidateClassImages', {
          count: imageFiles.length,
          productClass: productClass?.productClass,
        })
        setCandidateClassImages([
          ...candidateClassImages,
          ...imageFiles.map((image) => ({
            image,
            candidateClass: productClass?.productClass,
            mask: undefined,
            mapping: undefined,
          })),
        ])
      }

      // Handle zip files
      if (zipFiles.length > 0) {
        console.log(LOG_PREFIX, 'Processing zip files', {
          zipFileNames: zipFiles.map((z) => z.name),
        })
        setValidatingZipFiles(zipFiles.map((zipFile) => zipFile.name))
        zipFiles.map(handleZipFile)
      }
    } else {
      console.log(
        LOG_PREFIX,
        'Processing files with mapping (segmentation/classification_patch)'
      )
      console.table([
        ...files.map((f) => ({ type: 'image', name: f.name })),
        ...(safeMasks ?? []).map((m) => ({ type: 'mask', name: m.name })),
        ...(mapping ? [{ type: 'mapping', name: mapping.name }] : []),
      ])
      if (imageFiles.length > 0) {
        const newImageGroup: UploadImageGroup = {
          images: files,
          productId,
          productClass: '',
          canidateProductClass: '',
          masks: safeMasks,
          mapping,
        }
        console.log(
          LOG_PREFIX,
          'Calling createObservation directly (with mapping)',
          {
            imagesCount: files.length,
            masksCount: safeMasks.length,
          }
        )
        createObservation({
          imageGroups: [newImageGroup],
        })
        cleanUp()
      }
    }
  }

  const cleanUp = (withDrop?: string) => {
    setCandidateClassImages([])
    setValidatingZipFiles([])
    setDroppedFiles([])
    setShowImageGroupsModal(false)
    setDropRejected(withDrop || '')
  }

  useEffect(() => {
    setIsValidating(validatingZipFiles.length > 0 || droppedFiles.length > 0)
  }, [droppedFiles, validatingZipFiles])

  useEffect(() => {
    setIsDropzoneDisabled(isValidating)
  }, [isValidating])

  useEffect(() => {
    if (validatingZipFiles.length === 0 && candidateClassImages.length > 0) {
      setShowImageGroupsModal(true)
    }
  }, [candidateClassImages, validatingZipFiles])

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragReject,
    isFocused,
  } = useDropzone({
    accept: ALLOWED_MIME_TYPES.join(', '),
    disabled: isDropzoneDisabled,
    minSize: MIN_FILE_SIZE,
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES,
    multiple: true,
    onDragEnter: () => {
      if (!isDropzoneDisabled) {
        setCandidateClassImages([])
        setValidatingZipFiles([])
      }
    },
    onDrop: (acceptedFiles, rejectedFiles) => {
      console.log(LOG_PREFIX, 'onDrop called', {
        acceptedFilesCount: acceptedFiles.length,
        rejectedFilesCount: rejectedFiles.length,
        acceptedFiles: acceptedFiles.map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type,
        })),
        rejectedFiles: rejectedFiles.map((f) => ({
          file: f.file.name,
          errors: f.errors.map((e) => e.message),
        })),
      })

      setDropRejected('')
      if (rejectedFiles.length > 0) {
        console.warn(LOG_PREFIX, 'Files rejected', {
          rejectedFiles: rejectedFiles.map((f) => ({
            file: f.file.name,
            errors: f.errors,
          })),
        })
        cleanUp(
          t(
            'Invalid file selected: maxfiles={{maxfiles}} maxsize={{maxsize}} GB',
            {
              maxsize: MAX_FILE_SIZE / (1024 * 1024 * 1024),
              maxfiles: MAX_FILES,
            }
          )
        )
      } else {
        console.log(LOG_PREFIX, 'Setting dropped files', {
          count: acceptedFiles.length,
        })
        setDroppedFiles(acceptedFiles)
      }
    },
  })

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(isDropzoneDisabled ? disabledStyle : {}),
      ...(isFocused ? focusedStyle : {}),
    }),
    [isDragAccept, isDragReject, isFocused, isDropzoneDisabled]
  )

  return (
    <div>
      {showImageGroupsModal && (
        <ImageGroupsModal
          candidateClassImages={candidateClassImages}
          productId={productId}
          withClass={withProductClass}
          isLoading={validatingZipFiles.length > 0}
          onConfirm={(confirmedImageGroups: UploadImageGroup[]) => {
            createObservation({
              imageGroups: confirmedImageGroups,
            })
            cleanUp()
          }}
          onClose={() => cleanUp()}
        />
      )}
      <div className={classes.wrapper}>
        <div data-testid="dropzoneInput" {...getRootProps({ style })}>
          <input {...getInputProps()} />
          <div>
            {(isValidating && (
              <>
                <div className={classes.dropMessage}>
                  <FontAwesomeIcon
                    className={classes.loadingIcon}
                    icon={faCircleNotch}
                    spin
                  />
                  {uploadStatus}
                </div>
                <div className={classes.filetypes}>{t('Please wait...')}</div>
              </>
            )) || (
              <>
                <div className={classes.dropMessage}>
                  {productClass ? (
                    t(
                      `Drag and drop images with '{{className}}' class (or click to select them).`,
                      { className: productClass.productClass }
                    )
                  ) : (
                    <>
                      {t(
                        'Drag and drop some files here (or click to select files).'
                      )}
                      {(productType === 'segmentation' ||
                        productType === 'classification_patch') && (
                        <>
                          <br />
                          {t(
                            'You can also import custom annotations via zip file with a predefined structure (see details in end user manual).'
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className={classes.filetypes}>
                  {t('Allowed filetypes: {{filetypes}}.', {
                    filetypes: ALLOWED_TYPES,
                  })}
                </div>
                {dropRejected && (
                  <div className={classes.dropReject}>{dropRejected}</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UploadObservations
